import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

const srcPathReplacements = [
  ['/2x/', '/2x_webp/'],
  [/[^.]+$/, 'webp'],
  ['default', 'webp'],
];

const getSrcSet = (src) => srcPathReplacements
  .reduce((srcSet, replacement) => srcSet.replace(...replacement), src);

const WebpLazyImage = forwardRef(({
  className,
  error,
  fallbackSrc,
  onError,
  onLoad,
  imageAlt,
  isBrowserLazyLoading,
  loaded,
  src,
  imageInViewport,
  handleLoadSrcImg,
  ...rest
}, ref) => {
  const img = isBrowserLazyLoading
    ? (
      <img
        loading="lazy"
        src={(!error ? src : fallbackSrc) || fallbackSrc}
        alt={imageAlt}
        ref={ref}
        onLoad={onLoad}
        onError={onError}
        className={classnames(className, 'img-lazy', {
          'img-loaded': loaded && !error,
          'img-error': error,
        })}
        {...rest}
      />
    )
    : (
      <img
        src={handleLoadSrcImg()}
        alt={imageAlt}
        className={classnames(className, 'img-lazy', {
          'img-loaded': loaded,
          'img-error': error,
        })}
        ref={ref}
        {...rest}
      />
    );

  const shouldPreloadImage = imageInViewport && !isBrowserLazyLoading && !loaded && !error;

  return (
    <picture>
      { (isBrowserLazyLoading || loaded || shouldPreloadImage) && (
        <source srcSet={getSrcSet(src)} type="image/webp" />
      )}
      { shouldPreloadImage && (
        <img
          src={src}
          className="hidden"
          alt=""
          onLoad={onLoad}
          onError={onError}
        />
      )}
      {img}
    </picture>
  );
});

WebpLazyImage.propTypes = {
  className: PropTypes.string.isRequired,
  error: PropTypes.bool.isRequired,
  fallbackSrc: PropTypes.string.isRequired,
  onError: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  imageAlt: PropTypes.string.isRequired,
  isBrowserLazyLoading: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  src: PropTypes.string.isRequired,
  imageInViewport: PropTypes.bool.isRequired,
  handleLoadSrcImg: PropTypes.func.isRequired,
};

export default WebpLazyImage;
